import { message } from "antd";
import { useEffect, useState } from "react";

import { Caracteristicas } from "../../../interfaces/Caracteristicas";
import {
  cadastrarCaracteristicaService,
  CaracteristicaPayload,
} from "../../../services/cadastrarCaracteristicaService";
import { atualizarCaracteristicaService } from "../../../services/atualizarCaracteristicaService";
import { excluirCaracteristicaService } from "../../../services/excluirCaracteristicaService";
import { listarCaracteristicasService } from "../../../services/listarCaracteristicasService";
import { useError } from "../../../uteis/mensagens/useError";

export type EscopoCaracteristicaAdmin =
  | "AMBOS"
  | "EMPREENDIMENTO"
  | "APARTAMENTO";

const normalizarLista = (items: Caracteristicas[]) =>
  items.filter((item) => item.active !== 0);

export const useGerenciarCaracteristicas = () => {
  const [caracteristicas, setCaracteristicas] = useState<Caracteristicas[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const { showError, contextHolder } = useError();

  const carregarCaracteristicas = async () => {
    setLoading(true);

    try {
      const data = await listarCaracteristicasService();
      setCaracteristicas(normalizarLista(data));
    } catch (error) {
      showError("Erro ao carregar características");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarCaracteristicas();
  }, []);

  const atualizarCampo = (
    id: number,
    campo: "nome" | "escopo",
    valor: string
  ) => {
    setCaracteristicas((estadoAtual) =>
      estadoAtual.map((item) =>
        item.id === id ? { ...item, [campo]: valor } : item
      )
    );
  };

  const salvarCaracteristica = async (item: Caracteristicas) => {
    setSavingId(item.id);

    try {
      const payload = {
        nome: item.nome?.trim(),
        escopo: item.escopo || "AMBOS",
      };

      if (!payload.nome) {
        showError("Informe o nome da característica");
        return;
      }

      const response = await atualizarCaracteristicaService({
        id: item.id,
        nome: payload.nome,
        escopo: payload.escopo,
        active: item.active ?? 1,
      });

      if (!response) {
        showError("Não foi possível salvar a característica");
        return;
      }

      message.success("Característica salva com sucesso!");
      await carregarCaracteristicas();
    } catch (error) {
      showError("Não foi possível salvar a característica");
    } finally {
      setSavingId(null);
    }
  };

  const adicionarCaracteristica = async (payload: CaracteristicaPayload) => {
    setAdding(true);

    try {
      const response = await cadastrarCaracteristicaService({
        nome: payload.nome.trim(),
        escopo: payload.escopo || "AMBOS",
      });

      if (!response) {
        showError("Não foi possível adicionar a característica");
        return false;
      }

      message.success("Característica adicionada com sucesso!");
      await carregarCaracteristicas();
      return true;
    } catch (error) {
      showError("Não foi possível adicionar a característica");
      return false;
    } finally {
      setAdding(false);
    }
  };

  const removerCaracteristica = async (id: number) => {
    setDeletingId(id);

    try {
      const response = await excluirCaracteristicaService(id);

      if (!response) {
        showError("Não foi possível excluir a característica");
        return;
      }

      message.success("Característica excluída com sucesso!");
      await carregarCaracteristicas();
    } catch (error) {
      showError("Não foi possível excluir a característica");
    } finally {
      setDeletingId(null);
    }
  };

  return {
    caracteristicas,
    loading,
    savingId,
    deletingId,
    adding,
    contextHolder,
    atualizarCampo,
    salvarCaracteristica,
    adicionarCaracteristica,
    removerCaracteristica,
  };
};
